<template>
  <div class="filter-box bg-white sticky top-16 z-navbar md:z-1">
    <product-sub-categories v-model="subCategoryId" />
    <div class="container mx-auto bg-white">
      <search-filter v-model="filters" :product-count-query-params="queryParams" @submit="refreshProductList" @back-forward="fetchProducts" />
    </div>
  </div>
  <div class="product-list-container container mx-auto bg-white rounded md:mt-6 md:pb-5">
    <div
      v-if="productIds.length || isProductListFetching"
      class="products-list grid gap-4 grid-cols-1 p-5 pt-6 lg:gap-x-6 lg:gap-y-8 lg:grid-cols-3 lg:mx-auto lg:pt-5 lg:pb-8 xl:max-w-252 xl:px-0"
    >
      <template v-if="isProductListFetching">
        <product-card-skeleton v-for="i in 20" :key="i" />
      </template>
      <template v-else>
        <product-card v-for="productId in productIds" :key="productId" :productId="productId" :max-slots-count="MAX_TIME_SLOTS_COUNT" />
      </template>
    </div>
    <div v-if="productIds.length" class="navigator flex justify-center">
      <div class="description"></div>
      <div class="paging mb-10 lg:mt-15">
        <etg-pagination v-model:page="pagingState.page" :page-size="pagingState.size" :total-count="totalCount" />
      </div>
    </div>
    <list-no-result v-else class="px-5 md:px-0">
      <span>{{ $t('branch_list.empty.title') }}</span>
      <span>{{ $t('branch_list.empty.content') }}</span>
    </list-no-result>
  </div>
</template>

<script setup lang="ts">
import ListNoResult from '@/components/common/list-no-result.vue'
import EtgPagination from '@/components/common/etg-pagination.vue'
import ProductSubCategories from '@/components/product/product-sub-categories.vue'
import ProductCard from '@/components/product/product-card.vue'
import ProductCardSkeleton from '@/components/product/product-card-skeleton.vue'
import SearchFilter from '@/components/search/filter/index.vue'
import { AsyncDataKeys } from '@/constants/async-data-keys'

import { getRestaurants } from '@/service/branch'

import { useAppStore } from '@/store/app'
import { useBranchStore } from '@/store/branch'

import type { GetProductsReqParams } from '@/types/product'
import { SortRuleEnum } from '@/types/product'
import { FilterTypeEnum, type FilterComponentProps } from '@/types/filters'
import { GtmEvent } from '@/types/gtm'

import { QueryStringKeys, ALL_SUB_CATEGORY_ID } from '@/constants/commons'

const PAGE_SIZE = 21
const MAX_TIME_SLOTS_COUNT = 20

const nuxtApp = useNuxtApp()

const route = useRoute()

const appStore = useAppStore()
const branchStore = useBranchStore()

const { tagId, categoryId } = useCategory()
const { pagingState } = usePagination({ pageSize: PAGE_SIZE })

const subCategoryId = ref(0)
const filters = ref<FilterComponentProps>({ ...getDefaultFilters() })

const regionId = computed(() => Number(route.params.rid))
const keyword = computed(() => route.query[QueryStringKeys.SEARCH_KEYWORD])
const suggestionContext = computed(() => route.query[QueryStringKeys.SUGGESTION_CONTEXT])

const queryParams = computed(() => {
  return {
    filter_regionid: regionId.value,
    ...(suggestionContext.value ? { [QueryStringKeys.SUGGESTION_CONTEXT]: suggestionContext.value } : { filter_keyword: keyword.value }),
    size: pagingState.size,
    start: (pagingState.page - 1) * pagingState.size,
    ...(subCategoryId.value !== 0 && { sub_category_id: subCategoryId.value }),
    ...(categoryId.value ? { category_id: categoryId.value } : {}),
    ...(!!tagId.value && { filter_tag: tagId.value }),
    ...filters.value,
    ...(filters.value.filter_tags
      ? {
          filter_tags: nestedArrToString([...(filters.value.filter_tags ? filters.value.filter_tags : [])]),
        }
      : {}),
    ...appStore.geolocation,
  } as GetProductsReqParams
})

const getRestaurantsWithDebounce = useDebounceFn(getRestaurants, 300)

const {
  data: productsRes,
  pending: isProductListFetching,
  refresh: fetchProducts,
} = await useAsyncData(
  useAsyncDataKey(AsyncDataKeys.PRODUCT_LIST, [String(keyword.value || ''), regionId.value, categoryId.value]),
  async () => {
    const {
      data: { list: products, total_count },
    } = await getRestaurantsWithDebounce(queryParams.value)

    branchStore.setBranches('searchProduct', products)

    return { products, total_count }
  },
  { watch: [keyword, regionId, categoryId] }
)

const productIds = computed(() => branchStore.listIds.searchProduct || [])
const totalCount = computed(() => productsRes.value?.total_count || 100)

watch(subCategoryId, newId => {
  refreshProductList()
  if (newId === ALL_SUB_CATEGORY_ID) {
    nuxtApp.$trackEvent(GtmEvent.CATEGORY_VIEWED)
  }
})

watch([keyword, pagingState], () => {
  fetchProducts()
})

function getDefaultFilters() {
  const sortBy = appStore.isLocationAcquired ? SortRuleEnum.DISTANCE : SortRuleEnum.POPULAR
  const filterState = getFilterQueryString(route.query)
  const { restFilter, ...others } = filterState

  return trimObject({
    ...others,
    [FilterTypeEnum.SORTBY]: others[FilterTypeEnum.SORTBY] || sortBy,
    ...(!!restFilter && { filter_tags: trimNestedArray([...Object.values(restFilter)]) }),
  })
}

function refreshProductList() {
  fetchProducts()
  pagingState.page = 1
}
</script>

<style lang="scss" scoped>
$box-shadow: 0px 5px 15px 0px rgba(0, 0, 0, 0.1);

.filter-box {
  @screen md {
    box-shadow: $box-shadow;
  }

  &:after {
    content: '';
    box-shadow: $box-shadow;
    @apply absolute left-0 right-0 bottom-0 h-15px -z-1;

    @screen md {
      content: none;
    }
  }
}
</style>
